import { useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

/**
 * SEO Meta 数据结构
 */
export interface SeoMetaData {
  title: string;
  description: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogImageAlt?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  twitterSite?: string;
  canonicalUrl?: string;
  noIndex?: boolean;
  noFollow?: boolean;
  structuredData?: object[];
  hreflang?: Array<{locale: string; url: string}>;
}

/**
 * useSeoMeta Hook 配置选项
 */
/** 支持的 i18n 命名空间类型 */
type SupportedNamespace =
  | "common"
  | "admin"
  | "login"
  | "register"
  | "assessments"
  | "security"
  | "tanks"
  | "consent"
  | "home";

/**
 * useSeoMeta Hook 配置选项
 */
export interface UseSeoMetaOptions {
  /** i18n 命名空间 */
  namespace: SupportedNamespace;
  /** 从 API 响应获取的 meta 数据（优先级最高） */
  apiMeta?: Partial<SeoMetaData>;
  /** 是否禁用自动更新（用于服务端已注入 meta 的情况） */
  skipInitialUpdate?: boolean;
}

/** 站点名称 */
const SITE_NAME = "Tunzo";

/** 语言代码映射到 HTML lang 属性 */
const LANG_MAP: Record<string, string> = {
  zh: "zh-CN",
  en: "en",
  jp: "ja",
};

/** SEO 分析配置 */
export const SEO_CONFIG = {
  titleMaxLength: 60,
  descriptionMaxLength: 160,
  keywordsMaxCount: 10,
  ogImageMinWidth: 1200,
  ogImageMinHeight: 630,
  twitterImageMinWidth: 1200,
  twitterImageMinHeight: 675,
} as const;

/** SEO 质量检查结果 */
export interface SeoQualityResult {
  score: number;
  issues: string[];
  suggestions: string[];
  passed: boolean;
}

/**
 * 更新单个 meta 标签
 * @param name meta 标签的 name 或 property 属性值
 * @param content meta 标签的 content 值
 * @param attr 使用 name 还是 property 属性
 */
function updateMetaTag(
  name: string,
  content: string | undefined,
  attr: "name" | "property" = "name"
): void {
  if (!content) return;

  let meta = document.querySelector(`meta[${attr}="${name}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute(attr, name);
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", content);
}

/**
 * SEO Meta 标签管理 Hook
 *
 * 数据优先级：
 * 1. apiMeta（API 响应中的 meta 字段）
 * 2. 服务端注入的初始数据（#seo-meta-data）
 * 3. i18n 翻译文件
 *
 * @example
 * ```tsx
 * function AssessmentsLanding() {
 *   useSeoMeta({ namespace: 'assessments' });
 *   return <div>...</div>;
 * }
 * ```
 */
export function useSeoMeta({
  namespace,
  apiMeta,
  skipInitialUpdate = false,
}: UseSeoMetaOptions) {
  const { t, i18n } = useTranslation(namespace);
  const initialUpdateDone = useRef(false);

  /**
   * 获取服务端注入的初始 SEO 数据
   * 服务端通过 <script type="application/json" id="seo-meta-data"> 注入
   */
  const getServerSeoData = useCallback((): Partial<SeoMetaData> | null => {
    if (typeof document === "undefined") return null;

    try {
      const script = document.getElementById("seo-meta-data");
      if (script?.textContent) {
        return JSON.parse(script.textContent);
      }
    } catch (e) {
      console.warn("[useSeoMeta] Failed to parse server SEO data:", e);
    }
    return null;
  }, []);

  /**
   * 从 i18n 翻译文件获取 SEO 数据
   */
  const getI18nSeoData = useCallback((): SeoMetaData => {
    return {
      title: t("seo.title", { defaultValue: "" }),
      description: t("seo.description", { defaultValue: "" }),
      keywords: t("seo.keywords", { defaultValue: "" }),
      ogTitle: t("seo.og_title", { defaultValue: "" }),
      ogDescription: t("seo.og_description", { defaultValue: "" }),
      ogImage: t("seo.og_image", { defaultValue: "" }),
      twitterTitle: t("seo.twitter_title", { defaultValue: "" }),
      twitterDescription: t("seo.twitter_description", { defaultValue: "" }),
      twitterImage: t("seo.twitter_image", { defaultValue: "" }),
    };
  }, [t]);

  /**
   * 合并 SEO 数据（按优先级）
   * 优先级：apiMeta > serverData > i18nData
   */
  const getSeoData = useCallback((): SeoMetaData => {
    const serverData = getServerSeoData();
    const i18nData = getI18nSeoData();

    return {
      title: apiMeta?.title || serverData?.title || i18nData.title,
      description:
        apiMeta?.description || serverData?.description || i18nData.description,
      keywords: apiMeta?.keywords || serverData?.keywords || i18nData.keywords,
      ogTitle: apiMeta?.ogTitle || serverData?.ogTitle || i18nData.ogTitle,
      ogDescription:
        apiMeta?.ogDescription ||
        serverData?.ogDescription ||
        i18nData.ogDescription,
      ogImage: apiMeta?.ogImage || serverData?.ogImage || i18nData.ogImage,
      twitterTitle:
        apiMeta?.twitterTitle ||
        serverData?.twitterTitle ||
        i18nData.twitterTitle,
      twitterDescription:
        apiMeta?.twitterDescription ||
        serverData?.twitterDescription ||
        i18nData.twitterDescription,
      twitterImage:
        apiMeta?.twitterImage ||
        serverData?.twitterImage ||
        i18nData.twitterImage,
    };
  }, [apiMeta, getServerSeoData, getI18nSeoData]);

  /**
   * 更新所有 meta 标签
   */
  const updateMetaTags = useCallback(() => {
    if (typeof document === "undefined") return;

    const data = getSeoData();

    // 更新 document.title
    if (data.title) {
      document.title = `${data.title} | ${SITE_NAME}`;
    }

    // 更新基础 meta 标签
    if (data.description) {
      updateMetaTag("description", data.description);
    }
    if (data.keywords) {
      updateMetaTag("keywords", data.keywords);
    }

    // SEO 指令标签
    if (data.noIndex) {
      updateMetaTag("robots", "noindex,nofollow");
    } else if (data.noFollow) {
      updateMetaTag("robots", "nofollow");
    }

    // Canonical URL
    if (data.canonicalUrl) {
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", data.canonicalUrl);
    }

    // 更新 Open Graph 标签
    updateMetaTag("og:title", data.ogTitle || data.title, "property");
    updateMetaTag(
      "og:description",
      data.ogDescription || data.description,
      "property"
    );
    if (data.ogImage) {
      updateMetaTag("og:image", data.ogImage, "property");
      if (data.ogImageAlt) {
        updateMetaTag("og:image:alt", data.ogImageAlt, "property");
      }
    }
    updateMetaTag("og:url", window.location.href, "property");
    updateMetaTag("og:type", "website", "property");
    updateMetaTag("og:site_name", SITE_NAME, "property");

    // 更新 Twitter Card 标签
    updateMetaTag("twitter:card", "summary_large_image");
    updateMetaTag("twitter:title", data.twitterTitle || data.title);
    updateMetaTag(
      "twitter:description",
      data.twitterDescription || data.description
    );
    if (data.twitterImage || data.ogImage) {
      updateMetaTag("twitter:image", data.twitterImage || data.ogImage);
    }
    if (data.twitterSite) {
      updateMetaTag("twitter:site", data.twitterSite);
    }

    // 更新 hreflang 标签
    if (data.hreflang) {
      // 移除现有的 hreflang 标签
      document.querySelectorAll('link[rel="alternate"]').forEach(el => el.remove());

      // 添加新的 hreflang 标签
      data.hreflang.forEach(({locale, url}) => {
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", locale);
        link.setAttribute("href", url);
        document.head.appendChild(link);
      });
    }

    // 更新 HTML lang 属性
    const currentLang = i18n.language.split("-")[0].toLowerCase();
    document.documentElement.lang = LANG_MAP[currentLang] || currentLang;

    // 注入结构化数据
    if (data.structuredData) {
      // 移除现有的 JSON-LD
      document.querySelectorAll('script[type="application/ld+json"]').forEach(el => el.remove());

      // 添加新的结构化数据
      data.structuredData.forEach(schema => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }
  }, [getSeoData, i18n.language]);

  // 初始渲染时更新 meta 标签
  useEffect(() => {
    if (skipInitialUpdate && !initialUpdateDone.current) {
      initialUpdateDone.current = true;
      return;
    }

    updateMetaTags();
  }, [updateMetaTags, skipInitialUpdate]);

  // 监听语言变化，自动更新 meta 标签
  useEffect(() => {
    const handleLanguageChange = () => {
      updateMetaTags();
    };

    i18n.on("languageChanged", handleLanguageChange);
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n, updateMetaTags]);

  /**
   * SEO 质量分析
   */
  const analyzeSeoQuality = useCallback((data?: SeoMetaData): SeoQualityResult => {
    const targetData = data || getSeoData();
    const issues: string[] = [];
    const suggestions: string[] = [];

    // 标题检查
    if (!targetData.title) {
      issues.push("缺少标题");
    } else if (targetData.title.length > SEO_CONFIG.titleMaxLength) {
      issues.push(`标题过长 (${targetData.title.length}/${SEO_CONFIG.titleMaxLength})`);
      suggestions.push("缩短标题以避免搜索引擎截断");
    } else if (targetData.title.length < 30) {
      suggestions.push("标题可以更详细一些");
    }

    // 描述检查
    if (!targetData.description) {
      issues.push("缺少描述");
    } else if (targetData.description.length > SEO_CONFIG.descriptionMaxLength) {
      issues.push(`描述过长 (${targetData.description.length}/${SEO_CONFIG.descriptionMaxLength})`);
      suggestions.push("缩短描述以避免搜索引擎截断");
    } else if (targetData.description.length < 120) {
      suggestions.push("描述可以更详细一些");
    }

    // 关键词检查
    if (targetData.keywords) {
      const keywordCount = targetData.keywords.split(',').length;
      if (keywordCount > SEO_CONFIG.keywordsMaxCount) {
        issues.push(`关键词过多 (${keywordCount}/${SEO_CONFIG.keywordsMaxCount})`);
        suggestions.push("减少关键词数量，专注于最重要的几个");
      }
    }

    // Open Graph 检查
    if (!targetData.ogImage) {
      issues.push("缺少 Open Graph 图片");
      suggestions.push("添加合适的 OG 图片以改善社交媒体分享效果");
    }

    // 计算分数 (0-100)
    const issueWeight = 20;
    const suggestionWeight = 5;
    const baseScore = 100;
    const score = Math.max(0, baseScore - (issues.length * issueWeight) - (suggestions.length * suggestionWeight));

    return {
      score,
      issues,
      suggestions,
      passed: issues.length === 0,
    };
  }, [getSeoData]);

  /**
   * SEO 性能监控
   */
  const trackSeoMetrics = useCallback(() => {
    if (typeof window === "undefined" || !window.gtag) return;

    const quality = analyzeSeoQuality();

    // 发送 SEO 质量指标到 Google Analytics
    window.gtag('event', 'seo_quality_check', {
      event_category: 'SEO',
      event_label: namespace,
      value: quality.score,
      custom_parameters: {
        has_issues: quality.issues.length > 0,
        issue_count: quality.issues.length,
        suggestion_count: quality.suggestions.length,
      }
    });

    // 如果有严重问题，记录警告
    if (!quality.passed) {
      console.warn('[SEO] Quality issues detected:', quality.issues);
    }
  }, [namespace, analyzeSeoQuality]);

  // 在开发环境下自动检查 SEO 质量
  useEffect(() => {
    if (import.meta.env.DEV) {
      const timer = setTimeout(trackSeoMetrics, 1000);
      return () => clearTimeout(timer);
    }
  }, [trackSeoMetrics]);

  // 返回手动更新函数和获取数据函数，供需要时调用
  return {
    /** 手动更新所有 meta 标签 */
    updateMetaTags,
    /** 获取当前合并后的 SEO 数据 */
    getSeoData,
    /** 分析 SEO 质量 */
    analyzeSeoQuality,
    /** 跟踪 SEO 指标 */
    trackSeoMetrics,
  };
}
